import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import SidePanel from '../components/SidePanel';
import CreateLandmarkModal from '../components/CreateLandmarkModal';

const AdminLandmarkPage = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/api/landmark-projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to fetch landmark projects');
        if (response.status === 401) {
          navigate('/login');
        }
      }
    } catch (error) {
      setError('Error fetching landmark projects');
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [navigate]);

  const handleCreate = () => {
    setSelectedProject(null);
    setIsModalOpen(true);
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BASE_URL_API}/api/landmark-projects/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      fetchProjects();
    } catch (error) {
      setError('Error deleting landmark project');
    }
  };

  const handleSave = async (formData) => {
    const token = localStorage.getItem('token');
    const method = selectedProject ? 'PUT' : 'POST';
    const url = selectedProject
      ? `${process.env.REACT_APP_BASE_URL_API}/api/landmark-projects/${selectedProject.id}`
      : `${process.env.REACT_APP_BASE_URL_API}/api/landmark-projects`;

    const body = new FormData();
    body.append('title', formData.title);
    body.append('description', formData.description);
    body.append('location', formData.location);
    body.append('completionDate', formData.completionDate);
    if (formData.images) {
      formData.images.forEach(image => {
        body.append('images', image);
      });
    }

    try {
      await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body,
      });
      fetchProjects();
      setIsModalOpen(false);
    } catch (error) {
      setError('Error saving landmark project');
    }
  };

  return (
    <div className="flex h-screen bg-[#f4f7fa]">
      <SidePanel handleLogout={handleLogout} />
      <div className="flex-1 p-10">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-[#364A9A] font-poppins">Landmark Projects</h1>
          <button onClick={handleCreate} className="bg-green-500 text-white font-bold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition duration-300 ease-in-out transform hover:scale-105 font-poppins">
            Create New
          </button>
        </div>
        {error && <p className="text-red-500 text-center mb-4">{error}</p>}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">Title</th>
                  <th className="py-2 px-4 border-b">Location</th>
                  <th className="py-2 px-4 border-b">Completion Date</th>
                  <th className="py-2 px-4 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td className="py-2 px-4 border-b">{project.title}</td>
                    <td className="py-2 px-4 border-b">{project.location}</td>
                    <td className="py-2 px-4 border-b">{new Date(project.completionDate).toLocaleDateString()}</td>
                    <td className="py-2 px-4 border-b">
                      <button onClick={() => handleEdit(project)} className="bg-blue-500 text-white font-bold py-1 px-2 rounded-lg mr-2">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(project.id)} className="bg-red-500 text-white font-bold py-1 px-2 rounded-lg">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <CreateLandmarkModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} project={selectedProject} />
    </div>
  );
};

export default AdminLandmarkPage;
